import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { provideIcons } from "@ng-icons/core";
import { lucideLoaderCircle } from "@ng-icons/lucide";
import { BrnInputOtpComponent } from "@spartan-ng/brain/input-otp";
import { HlmButtonDirective } from "@spartan-ng/ui-button-helm";
import {
  HlmCardContentDirective,
  HlmCardDirective,
  HlmCardFooterDirective,
} from "@spartan-ng/ui-card-helm";
import { HlmFormFieldModule } from "@spartan-ng/ui-formfield-helm";
import { HlmInputDirective } from "@spartan-ng/ui-input-helm";
import { HlmLabelDirective } from "@spartan-ng/ui-label-helm";
import { HlmSpinnerComponent } from "@spartan-ng/ui-spinner-helm";
import {
  HlmTabsComponent,
  HlmTabsContentDirective,
} from "@spartan-ng/ui-tabs-helm";
import {
  HlmInputOtpDirective,
  HlmInputOtpGroupDirective,
  HlmInputOtpSlotComponent,
} from "../../../../../ui/ui-inputotp-helm/src";
import { AuthenticateStateEnum, SignInEnum } from "../../../enums/authenticate.enum";
import { CognitoSignInResult } from "../../../interfaces/auth.interfaces";
import { AuthService } from "../../../services/auth.service";

@Component({
  selector: "keus-confirm-sign-up",
  imports: [
    CommonModule,
    HlmButtonDirective,
    HlmInputDirective,
    FormsModule,
    ReactiveFormsModule,
    HlmLabelDirective,
    HlmTabsComponent,
    HlmTabsContentDirective,
    HlmCardContentDirective,
    HlmCardDirective,
    HlmCardFooterDirective,
    HlmFormFieldModule,
    HlmSpinnerComponent,
    BrnInputOtpComponent,
    HlmInputOtpDirective,
    HlmInputOtpGroupDirective,
    HlmInputOtpSlotComponent,
  ],
  providers: [provideIcons({ lucideLoaderCircle })],
  host: {
    class: "block",
  },
  templateUrl: "./confirm-sign-up.component.html",
  styleUrl: "./confirm-sign-up.component.css",
})
export class ConfirmSignUpComponent implements AfterViewInit {
  /** Injected AuthService instance */
  private readonly _authService: AuthService = inject(AuthService);

  /** Reference to the OTP input component */
  @ViewChild(BrnInputOtpComponent, { read: ElementRef }) private otpElement!: ElementRef;

  /** Event emitted when the confirm sign up button is clicked */
  @Output() public readonly confirmSignUpEvent =
    new EventEmitter<AuthenticateStateEnum>();

  /** Form group for the confirm sign up form */
  public readonly form = new FormGroup({
    code: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ],
    }),
  });

  /** Whether the form is disabled. */
  public isDisabled = computed(() => {
    return !this._formValidSignal() || this.isLoading();
  });

  /** Whether the form is valid as a signal. */
  private readonly _formValidSignal = signal<boolean>(false);

  /** Signal indicating whether the confirm sign up form is loading */
  public readonly isLoading: WritableSignal<boolean> = signal(false);

  /** Overrides global formatDate (spartan)  */
  public readonly transformPaste = (pastedText: string) =>
    pastedText.replaceAll("-", "");

  ngOnInit() {
    if (this.form) {
      this._formValidSignal.set(this.form.valid);

      this.form.statusChanges.subscribe(() => {
        this._formValidSignal.set(this.form.valid);
      });
    }
  }

  ngAfterViewInit(): void {
    // Set focus on the OTP input after the view is initialized
    setTimeout(() => {
      if (this.otpElement) {
        // Find the first input element within the OTP component
        const inputElement = this.otpElement.nativeElement.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }
    }, 100);
  }

  /**
   * Function to confirm sign up
   */
  public confirmSignUp(): void {
    if (!this.form.valid) {
      this.form.controls.code.setErrors({ invalid: true });
      return;
    }
    const { code } = this.form.getRawValue();
    this.isLoading.set(true);

    this._authService.confirmSignUp$(code).subscribe({
      next: (result: CognitoSignInResult) => {
        // Handle the different possible results from confirmSignUp$
        switch (result.type) {
          case SignInEnum.SUCCESS:
            // User successfully confirmed and logged in
            this._signInEvent();
            break;
          case SignInEnum.TOTP_REQUIRED:
            // User confirmed but needs TOTP verification
            // You might want to emit a different event or handle TOTP flow here
            this._signInEvent();
            break;
          case SignInEnum.NEW_PASSWORD_REQUIRED:
            // User confirmed but needs to set a new password
            // You might want to emit a different event for password reset flow
            this._signInEvent();
            break;
          default:
            // Handle other cases if needed
            this._signInEvent();
            break;
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.form.controls.code.setErrors({ invalid: true });
        console.error(error);
      },
      complete: () => this.isLoading.set(false),
    });
  }

  /**
   * Function to resend confirmation code
   */
  public onResendCodeClick(): void {
    this.isLoading.set(true);

    this._authService.resendCode$().subscribe({
      error: (error) => {
        console.error(error);
        this.isLoading.set(false);
      },
      complete: () => this.isLoading.set(false),
    });
  }

  /**
   * Handle sign in event
   */
  private _signInEvent(): void {
    this.confirmSignUpEvent.emit(AuthenticateStateEnum.SIGN_IN_AND_SIGN_UP);
  }

}
